/**
 * Listing Wizard Store — Zustand ephemeral state for the guided vehicle creation flow.
 *
 * Holds draft data for each step, the current step index, and an optional vehicleId
 * for edit mode.
 *
 * PERSISTED (MMKV native / localStorage web). A host who shot 17 live photos and
 * scanned a document must not lose that work to an app restart, a crash or an
 * Android low-memory kill — which is exactly what happened on 2026-08-16, while
 * the UI promised "no tenés que empezar desde cero". Captures are copied out of
 * the evictable cache first (listing-photo-storage), so a persisted URI still
 * points at a real file. reset() clears both the state and those files.
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { createSplitPlatformStorage } from '../storage/zustand-storage'
import { listingPhotoExists, pruneListingPhotoFiles } from '../utils/listing-photo-storage'
import { expiredVehicleDocuments } from '../utils/vehicle-booking-blockers'
import { vehicleImageService } from '../services/vehicle-image.service'
import type {
  VehicleDetailsInput,
  VehicleFeaturesInput,
  VehiclePhotosInput,
  VehicleLocationInput,
  VehiclePricingInput,
} from '../validations/vehicle.schema'
import type {
  HostVehicle,
  HostVehicleDocumentItem,
  VehicleOwnershipDeclaration,
} from '../types/vehicle.types'
import type { VehicleDocumentInput } from '../types/vehicle-document.types'
import {
  LISTING_PHOTO_REQUIREMENT_IDS,
  MAX_PHOTOS_PER_REPEATABLE_SLOT,
  isRepeatablePhotoRequirement,
  type ListingPhoto,
  type ListingPhotoRequirementId,
  type StagedListingPhoto,
} from '../types/listing-photo.types'

// ─── Step Definitions ───────────────────────────────────────────────────────

export const WIZARD_STEPS = [
  'details',
  'ownership',
  'features',
  'photos',
  'location',
  'pricing',
  'documents',
  'review',
] as const

export type WizardStep = (typeof WIZARD_STEPS)[number]

/**
 * The explicit "whose car is this?" answer, asked up front in the wizard.
 *
 * Re-exported, not redeclared: it is now a column on `vehicles`, so the definition belongs with
 * the domain types and every surface must read the same one.
 */
export type { VehicleOwnershipType } from '../types/vehicle.types'

interface ListingDocumentReviewState {
  rejected: HostVehicleDocumentItem[]
  pending: HostVehicleDocumentItem[]
  /**
   * Aprobados pero con la fecha ya pasada. Es su propia categoría porque no es
   * un documento en revisión ni uno rechazado: es uno que estuvo bien y dejó de
   * estarlo, y mientras siga ahí la base rechaza toda reserva del vehículo.
   */
  expired: HostVehicleDocumentItem[]
}

// ─── State Shape ────────────────────────────────────────────────────────────

interface ListingWizardState {
  /** Current step index (0-based) */
  currentStep: number

  /** Vehicle ID when editing an existing listing (null for new) */
  vehicleId: string | null

  /**
   * How this wizard session was opened, latched once at mount.
   *
   * `vehicleId` alone cannot answer "is this an edit?": review.tsx sets it
   * mid-publish the moment the draft row is created, so a failed publish used
   * to relabel the whole wizard "Editar" and turn Publicar into Guardar.
   * `loadFromVehicle` (the only path that hydrates from a server row) is what
   * marks a session as an edit; `reset()` puts it back to 'create'.
   */
  entryMode: 'create' | 'edit'

  /**
   * Has the host changed anything since this draft was last loaded from the
   * server (or started fresh)?
   *
   * Needed because `hasUnsavedWork`-style checks cannot tell edits apart from
   * a merely-opened edit: `loadFromVehicle` fills `details`, `photos` and the
   * rest, so "there is content" is true the instant an edit screen mounts. Only
   * a flag set by the host-facing setters — and cleared by the two paths that
   * define a fresh baseline, `loadFromVehicle` and `beginEdit` — can answer
   * "would re-entering here throw away work?".
   *
   * Persisted: an edit interrupted by an app kill is exactly the case worth
   * asking about on the way back in.
   */
  dirtySinceLoad: boolean

  /** Step 1: Vehicle details (make, model, year, etc.) */
  details: Partial<VehicleDetailsInput>

  /** Step 2: Features, title, description */
  features: Partial<VehicleFeaturesInput>

  /** Step 3: Photos */
  photos: Partial<VehiclePhotosInput>

  /**
   * Local photo URIs already stored server-side for `vehicleId`.
   *
   * A failed publish leaves the wizard alive so the host can retry, but the
   * store still holds the original local `file://` URIs — nothing rewrites them
   * to the uploaded URLs. Without this list every retry re-uploaded all 17
   * inspection shots and the listing ended up with duplicate images.
   */
  uploadedPhotoUris: string[]

  /**
   * Captures whose bytes already travelled to the staging area, keyed by the DIGEST of their
   * local uri — on web the uri IS the photo (a data: string of megabytes), and this map
   * persists in the draft's light localStorage partition, the one the split-storage work
   * exists to keep small.
   *
   * Written by the background staging queue as each upload lands; read at publish, where a
   * matching entry turns that photo's upload into a server-side copy. Persisted with the draft
   * so an app restart does not forget which bytes are already up. NEVER user content: a retake
   * simply stops matching (new uri) and the stale entry is harmless, so nothing here marks the
   * draft dirty and losing this map costs bandwidth, not work.
   */
  stagedPhotos: Record<string, StagedListingPhoto>
  /** Document URIs already stored server-side, so a retry does not file them twice. */
  uploadedDocumentUris: string[]

  /**
   * A required document was filed as PENDING review rather than verified.
   *
   * Persisted because it is a fact about the SERVER row, not about one submit
   * attempt. It used to be a local let in handleSubmit, so a retry — which skips
   * the already-uploaded document and therefore never re-enters the catch that
   * set it — forgot it, asked for status active, and the DB completeness trigger
   * rejected it. Every retry after the first failed forever, identically.
   */
  documentsNeedReview: boolean

  /** Step 4: Location */
  location: Partial<VehicleLocationInput>

  /**
   * Did the HOST choose these coordinates, or did we?
   *
   * The location step forward-geocodes the typed address on Siguiente and overwrites homeLat /
   * homeLng with the result. That is right when the coordinates came from us — a device fix, a
   * map that centred itself — and wrong when the host dragged the pin onto their own gate: a
   * Salvadoran address rarely has a street number, so the geocoder answers with a neighbourhood
   * centroid and the guest is sent blocks away from the car.
   *
   * Coordinates alone cannot answer this: the map emits a camera change when it first centres
   * itself, with nobody having touched it. So provenance is recorded where the intent is known —
   * a real pointer gesture on the map, the "use my location" button, or a vehicle whose
   * coordinates a host already saved.
   */
  homeLocationPinnedByHost: boolean

  /** Step 5: Pricing */
  pricing: Partial<VehiclePricingInput>

  /** Documents (tarjeta de circulación and optional policy) */
  documents: Partial<VehicleDocumentInput>

  /** Whether the user chose to skip all documents (listing stays draft) */
  documentsSkipped: boolean

  /** Whether the user chose to submit registration now and policy later */
  policyDeferred: boolean

  /**
   * Set when the host jumped to a step from the REVIEW screen's "Editar".
   *
   * Not persisted: it describes one in-session navigation, not the draft. Back
   * from that step must return to Revisar instead of treating step 0 as "leave
   * the wizard", which trapped the host into re-walking all seven steps.
   */
  editReturnTo: WizardStep | null

  /** Current admin/document review context for edit deep links. */
  documentReview: ListingDocumentReviewState | null

  /** Company ownership — set when user confirms vehicle belongs to a company */
  companyDetection: {
    detected: boolean
    legalName: string | null
    userChoice: 'create_org' | 'join_org' | 'skip' | null
    organizationId: string | null
  } | null

  /**
   * Explicit ownership answer — asked up front instead of inferred silently
   * from the tarjeta. Drives the coverage path (mine/third_party → Qualitas;
   * company → agency) and the tarjeta-owner-vs-identity reconciliation.
   */
  ownership: VehicleOwnershipDeclaration | null

  // ── Actions ────────────────────────────────────────────────────────────

  /** Navigate to a specific step */
  goToStep: (step: number) => void

  /** Attach a created draft vehicle to the wizard so retries update it instead of starting over */
  setVehicleId: (vehicleId: string | null) => void

  /** Record photo URIs that finished uploading, so a retry skips them */
  markPhotosUploaded: (uris: string[]) => void

  /** Record a staged upload. Background-only writer; the capture flow never awaits it. */
  setStagedPhoto: (staged: StagedListingPhoto) => void
  markDocumentUploaded: (uri: string) => void
  setDocumentsNeedReview: (needsReview: boolean) => void
  setEditReturnTo: (step: WizardStep | null) => void

  /** Go to next step */
  nextStep: () => void

  /** Go to previous step */
  prevStep: () => void

  /** Update data for a specific step */
  setDetails: (data: Partial<VehicleDetailsInput>) => void
  setFeatures: (data: Partial<VehicleFeaturesInput>) => void
  setPhotos: (data: Partial<VehiclePhotosInput>) => void
  setLocation: (data: Partial<VehicleLocationInput>) => void
  /** Record that the coordinates now on file are the host's own choice, not ours. */
  setHomeLocationPinnedByHost: (pinned: boolean) => void
  setPricing: (data: Partial<VehiclePricingInput>) => void
  setDocuments: (data: Partial<VehicleDocumentInput>) => void
  setDocumentsSkipped: (skipped: boolean) => void
  setPolicyDeferred: (deferred: boolean) => void

  /** Update company detection state */
  setCompanyDetection: (data: ListingWizardState['companyDetection']) => void

  /** Set the explicit ownership answer ("whose car is this?"). */
  setOwnership: (data: ListingWizardState['ownership']) => void

  /** Populate store from an existing vehicle (edit mode) */
  loadFromVehicle: (vehicle: HostVehicle) => void

  /** Reset entire wizard to initial state (keeps captured files — see impl). */
  reset: () => void

  /** Explicit, user-confirmed discard: clears the draft AND deletes its files. */
  discardDraft: () => void

  /**
   * Enter EDIT mode for an existing listing, atomically.
   *
   * reset() followed by setVehicleId() is two writes, and persist flushes each
   * one synchronously to MMKV — so between them the DISK held
   * { entryMode: 'create', vehicleId: <a LIVE car> }. A crash or a kill in that
   * window made it durable, and the create-door resume gate would then hand that
   * live vehicleId to a new listing and overwrite the published row. One write.
   */
  beginEdit: (vehicleId: string) => void
}

/**
 * Does a resumable draft exist? One definition, so the entry points, the resume
 * affordance and the discard confirmation can never disagree about it.
 */
export function hasListingDraft(state: {
  details?: Partial<VehicleDetailsInput>
  photos?: Partial<VehiclePhotosInput>
  features?: Partial<VehicleFeaturesInput>
  vehicleId?: string | null
}): boolean {
  return Boolean(
    state.vehicleId ||
    (state.photos?.photos?.length ?? 0) > 0 ||
    state.details?.makeId ||
    state.details?.plateNumber ||
    state.features?.title,
  )
}

// ─── Initial State ──────────────────────────────────────────────────────────

const INITIAL_STATE = {
  currentStep: 0,
  vehicleId: null as string | null,
  entryMode: 'create' as 'create' | 'edit',
  dirtySinceLoad: false,
  details: {} as Partial<VehicleDetailsInput>,
  features: {} as Partial<VehicleFeaturesInput>,
  photos: {} as Partial<VehiclePhotosInput>,
  uploadedPhotoUris: [] as string[],
  stagedPhotos: {} as Record<string, StagedListingPhoto>,
  uploadedDocumentUris: [] as string[],
  documentsNeedReview: false,
  editReturnTo: null as WizardStep | null,
  location: {} as Partial<VehicleLocationInput>,
  homeLocationPinnedByHost: false,
  pricing: {} as Partial<VehiclePricingInput>,
  documents: {} as Partial<VehicleDocumentInput>,
  documentsSkipped: false,
  policyDeferred: true,
  documentReview: null as ListingDocumentReviewState | null,
  companyDetection: null,
  ownership: null,
}

function asListingPhotoRequirementId(value: string | null): ListingPhotoRequirementId | null {
  return LISTING_PHOTO_REQUIREMENT_IDS.includes(value as ListingPhotoRequirementId)
    ? (value as ListingPhotoRequirementId)
    : null
}

// ─── Store ──────────────────────────────────────────────────────────────────

/**
 * The document fields the server holds, mapped back onto the wizard step.
 *
 * Photo URIs are deliberately absent: they are local capture paths from the
 * original session and do not survive it, and the step requires a live capture
 * to change a photo regardless. Restoring the numbers and dates is what stops
 * an edit from re-asking; pretending the photos are still there would be a lie
 * the submit step would then have to catch.
 */
function hydrateDocumentsFromVehicle(
  documents: HostVehicle['documents'],
): Partial<VehicleDocumentInput> {
  const byType = (type: string) => documents.find((document) => document.documentType === type)
  const tarjeta = byType('tarjeta_circulacion_front') ?? byType('tarjeta_circulacion')
  const policy = byType('insurance_policy')

  return {
    ...(tarjeta?.documentNumber ? { documentNumber: tarjeta.documentNumber } : {}),
    ...(tarjeta?.expiryDate ? { expiryDate: tarjeta.expiryDate } : {}),
    ...(policy?.documentNumber ? { insurancePolicyNumber: policy.documentNumber } : {}),
    ...(policy?.expiryDate ? { insurancePolicyExpiryDate: policy.expiryDate } : {}),
  }
}

/**
 * What survives an app restart. Everything the host TYPED or CAPTURED, so a
 * crash, a swipe-away or an Android low-memory kill never costs 17 live photos
 * and a document scan again. `documentReview` is deliberately absent: it mirrors
 * server rows and is re-fetched, so persisting it would only let it go stale.
 */
type PersistedListingWizard = Omit<
  ListingWizardState,
  | 'documentReview'
  | 'goToStep'
  | 'nextStep'
  | 'prevStep'
  | 'setDetails'
  | 'setFeatures'
  | 'setPhotos'
  | 'setLocation'
  | 'setHomeLocationPinnedByHost'
  | 'setPricing'
  | 'setDocuments'
  | 'setDocumentsSkipped'
  | 'setPolicyDeferred'
  | 'setDocumentReview'
  | 'setCompanyDetection'
  | 'setOwnership'
  | 'setVehicleId'
  | 'markPhotosUploaded'
  | 'setStagedPhoto'
  | 'markDocumentUploaded'
  | 'setDocumentsNeedReview'
  | 'setEditReturnTo'
  | 'editReturnTo'
  | 'loadFromVehicle'
  | 'reset'
  | 'discardDraft'
  | 'beginEdit'
>

export const useListingWizardStore = create<ListingWizardState>()(
  persist(
    (set) => ({
      ...INITIAL_STATE,

      goToStep: (step) =>
        set({ currentStep: Math.max(0, Math.min(step, WIZARD_STEPS.length - 1)) }),

      setVehicleId: (vehicleId) => set({ vehicleId }),

      markPhotosUploaded: (uris) =>
        set((s) => ({ uploadedPhotoUris: Array.from(new Set([...s.uploadedPhotoUris, ...uris])) })),
      // No dirtySinceLoad: staging is plumbing, not the host's work. Marking the draft dirty
      // from a background upload would make the "unsaved changes?" question lie.
      setStagedPhoto: (staged) =>
        set((s) => ({ stagedPhotos: { ...s.stagedPhotos, [staged.sourceKey]: staged } })),
      setDocumentsNeedReview: (documentsNeedReview) => set({ documentsNeedReview }),

      setEditReturnTo: (editReturnTo) => set({ editReturnTo }),

      markDocumentUploaded: (uri) =>
        set((s) => ({
          uploadedDocumentUris: Array.from(new Set([...s.uploadedDocumentUris, uri])),
        })),

      nextStep: () =>
        set((s) => ({ currentStep: Math.min(s.currentStep + 1, WIZARD_STEPS.length - 1) })),

      prevStep: () => set((s) => ({ currentStep: Math.max(s.currentStep - 1, 0) })),

      // Every setter below carries host-entered work, so every one marks the
      // draft dirty. The bookkeeping setters above (goToStep, setVehicleId,
      // markPhotosUploaded, markDocumentUploaded, setDocumentsNeedReview,
      // setEditReturnTo) deliberately do NOT: they record what the app did, not
      // what the host typed, and treating them as edits would make a draft
      // "dirty" just for being opened.
      setDetails: (data) =>
        set((s) => ({ details: { ...s.details, ...data }, dirtySinceLoad: true })),
      setFeatures: (data) =>
        set((s) => ({ features: { ...s.features, ...data }, dirtySinceLoad: true })),
      setPhotos: (data) => set((s) => ({ photos: { ...s.photos, ...data }, dirtySinceLoad: true })),
      setLocation: (data) =>
        set((s) => ({ location: { ...s.location, ...data }, dirtySinceLoad: true })),
      setHomeLocationPinnedByHost: (homeLocationPinnedByHost) =>
        set({ homeLocationPinnedByHost, dirtySinceLoad: true }),
      setPricing: (data) =>
        set((s) => {
          // Validation also calls this setter when the host merely opens the
          // price section and continues. An identical result is not an edit.
          const unchanged = Object.entries(data).every(([key, value]) =>
            Object.is(s.pricing[key as keyof VehiclePricingInput], value),
          )
          return unchanged ? s : { pricing: { ...s.pricing, ...data }, dirtySinceLoad: true }
        }),
      setDocuments: (data) =>
        set((s) => ({ documents: { ...s.documents, ...data }, dirtySinceLoad: true })),
      setDocumentsSkipped: (documentsSkipped) => set({ documentsSkipped, dirtySinceLoad: true }),
      setPolicyDeferred: (policyDeferred) => set({ policyDeferred, dirtySinceLoad: true }),
      setCompanyDetection: (companyDetection) => set({ companyDetection, dirtySinceLoad: true }),
      setOwnership: (ownership) => set({ ownership, dirtySinceLoad: true }),

      loadFromVehicle: (vehicle) => {
        const reviewDocuments = vehicle.documents.filter((doc) =>
          ['pending', 'submitted', 'rejected'].includes(doc.status),
        )
        // Misma regla que el trigger `assert_vehicle_papers_are_current`, vía el
        // único util que la implementa. No se re-deriva acá.
        const expiredTypes = new Set(
          expiredVehicleDocuments(vehicle).map((doc) => doc.documentType),
        )

        set({
          vehicleId: vehicle.id,
          // The one and only path that means "opened to edit an existing listing".
          entryMode: 'edit',
          currentStep: 0,
          details: {
            makeId: vehicle.makeId,
            modelId: vehicle.modelId,
            year: vehicle.year,
            category: vehicle.category,
            color: vehicle.color,
            transmission: vehicle.transmission,
            fuelType: vehicle.fuelType,
            seats: vehicle.seats,
            doors: vehicle.doors,
            plateNumber: vehicle.plateNumber,
            plateCountry: vehicle.plateCountry,
            vin: vehicle.vin ?? undefined,
            odometerKm: vehicle.odometerKm,
          },
          features: {
            title: vehicle.title,
            description: vehicle.description ?? undefined,
            features: vehicle.features,
            rules: vehicle.rules ?? undefined,
            isInstantBook: vehicle.isInstantBook,
          },
          photos: {
            // Prefer stored Qualitas slot ids; fall back to position order so older
            // rows without requirement_id still open for edit without a 17-shot
            // re-capture for a price tweak.
            photos: vehicle.images
              .map((img, index): ListingPhoto | null => {
                if (!img.url) return null
                const requirementId =
                  asListingPhotoRequirementId(img.requirementId) ??
                  LISTING_PHOTO_REQUIREMENT_IDS[index] ??
                  null
                if (!requirementId) return null

                return {
                  uri: img.url,
                  width: img.width ?? undefined,
                  height: img.height ?? undefined,
                  capturedAt: img.capturedAt ?? vehicle.updatedAt ?? vehicle.createdAt,
                  source: 'tago_live_camera',
                  requirementId,
                }
              })
              .filter((photo): photo is ListingPhoto => photo !== null)
              // DEDUPE ON READ — this is what un-bricks a listing.
              //
              // vehicle_images has no unique key, so several paths could leave two
              // rows for one requirement (a retake on a live listing, a timed-out
              // upload whose retry ran alongside it). The client schema then demands
              // exactly one photo per slot, so the host could never edit or re-save
              // that car AGAIN — permanently, with no in-app control able to delete
              // the extra row. Keeping the newest per slot (repeatable slots keep
              // their cap) makes an already-duplicated listing editable again the
              // moment this ships, instead of waiting for a data repair.
              .reduce<ListingPhoto[]>((kept, photo) => {
                const sameSlot = kept.filter((p) => p.requirementId === photo.requirementId)
                const limit = isRepeatablePhotoRequirement(photo.requirementId)
                  ? MAX_PHOTOS_PER_REPEATABLE_SLOT
                  : 1
                if (sameSlot.length < limit) return [...kept, photo]
                // Over the cap: keep the NEWEST, drop the oldest of that slot.
                const oldest = sameSlot.reduce((a, b) => (a.capturedAt <= b.capturedAt ? a : b))
                if (photo.capturedAt <= oldest.capturedAt) return kept
                return [...kept.filter((p) => p !== oldest), photo]
              }, []),
          },
          // Remote URLs must not be re-uploaded on publish retry / edit save.
          uploadedPhotoUris: vehicle.images
            .map((img) => img.url)
            .filter((url): url is string => Boolean(url) && /^https?:\/\//i.test(url)),
          location: {
            homeAddress: vehicle.homeAddress ?? undefined,
            homeLat: vehicle.homeLocation?.lat,
            homeLng: vehicle.homeLocation?.lng,
            countryId: vehicle.countryId,
            cityId: vehicle.cityId ?? undefined,
            deliveryRadiusKm: vehicle.deliveryRadiusKm ?? undefined,
          },
          // A saved vehicle's coordinates are already a host's answer, so editing anything else
          // about the car must not quietly move it: without this, re-saving a listing to change
          // the price re-geocodes the address and relocates a car that was exactly where the
          // host had put it.
          homeLocationPinnedByHost:
            vehicle.homeLocation?.lat != null && vehicle.homeLocation?.lng != null,
          pricing: {
            dailyRateCents: vehicle.dailyRateCents,
            hourlyRateCents: vehicle.hourlyRateCents ?? undefined,
            weeklyDiscountPercent: vehicle.weeklyDiscountPercent ?? undefined,
            monthlyDiscountPercent: vehicle.monthlyDiscountPercent ?? undefined,
            securityDepositCents: vehicle.securityDepositCents ?? undefined,
            cleaningFeeCents: vehicle.cleaningFeeCents ?? undefined,
            dailyKmLimit: vehicle.dailyKmLimit ?? undefined,
            excessKmRateCents: vehicle.excessKmRateCents ?? undefined,
            minRentalHours: vehicle.minRentalHours ?? null,
            insuranceModel: vehicle.insuranceModel,
          },
          // Editing a listing used to arrive at the Documents step blank, with the
          // tarjeta number and both expiry dates sitting in the rows this same
          // function just loaded. So a host who came back to change the price was
          // asked to retype a document Tago had already verified.
          //
          // Only the fields the server actually holds are restored: the photo URIs
          // are local capture paths that do not survive, and the step's own guards
          // require a fresh live capture anyway if the photos are to change. What
          // this removes is the retyping, not the re-verification.
          documents: hydrateDocumentsFromVehicle(vehicle.documents),
          documentsSkipped: false,
          // Agencies that already filed their own policy open edit in "own policy" mode.
          // Individuals and Qualitas-path agencies stay deferred (no policy upload required).
          policyDeferred: !(
            Boolean(vehicle.organizationId) &&
            vehicle.documents.some((doc) => doc.documentType === 'insurance_policy')
          ),
          documentReview: {
            rejected: reviewDocuments.filter((doc) => doc.status === 'rejected'),
            pending: reviewDocuments.filter((doc) => doc.status !== 'rejected'),
            // Vencidos: `approved`, y por eso quedan FUERA de reviewDocuments,
            // que solo mira pending/submitted/rejected. Sin esta lista el paso
            // de documentos en modo edición trata un papel vencido como uno en
            // regla y deja pasar de largo — que es exactamente como cuatro
            // carros pasaron tres meses activos sin poder recibir una reserva.
            expired: vehicle.documents.filter((doc) => expiredTypes.has(doc.documentType)),
          },
          // Organization-backed vehicles are agency listings even when company OCR
          // never fired — edit mode must keep the own-policy / Qualitas choice.
          companyDetection: vehicle.organizationId
            ? {
                detected: vehicle.companyOwnerDetected ?? true,
                legalName: vehicle.detectedLegalName,
                userChoice: 'join_org' as const,
                organizationId: vehicle.organizationId,
              }
            : vehicle.companyOwnerDetected
              ? {
                  detected: true,
                  legalName: vehicle.detectedLegalName,
                  userChoice: 'create_org' as const,
                  organizationId: null,
                }
              : null,
          // THE HOST'S ANSWER, OR NOTHING. Never a guess.
          //
          // This used to read `organizationId ? 'company' : 'mine'`, so a host who had
          // declared "this car is not mine, X authorised me to rent it" had that replaced with
          // "mine" the next time they opened the listing to change the price. We overwrote a
          // person's statement about someone else's property with a value they never gave, and
          // the wizard then showed it back to them as their own answer.
          //
          // `null` for a listing older than the column is the honest state: the ownership step
          // asks again, and one real answer replaces a permanent blank.
          ownership: vehicle.ownership
            ? {
                type: vehicle.ownership.type,
                authorizedByName: vehicle.ownership.authorizedByName,
                relationship: vehicle.ownership.relationship,
              }
            : null,
          // A server load IS the new baseline: nothing here is host-entered work
          // that a re-entry could throw away.
          dirtySinceLoad: false,
        })
      },

      /**
       * Clear the wizard state. Deliberately does NOT delete the captured files.
       *
       * reset() is called from every "Agregar vehículo" entry point and from
       * reset-all on ANY sign-out — including the involuntary one an expired
       * refresh token triggers at app start. Deleting files here meant the button
       * a host taps to RESUME their draft was the button that destroyed 17
       * full-resolution captures, and a token expiry wiped them before the host
       * even touched the phone. State is cheap to rebuild; a live photo of a car
       * that is no longer in front of you is not.
       *
       * Files are removed only where we KNOW they are safe to remove:
       * discardDraft() (an explicit, confirmed discard) and after a successful
       * publish, where they are already on the server.
       */
      reset: () => set(INITIAL_STATE),

      /**
       * Enter edit mode for an existing vehicle in ONE write.
       *
       * (The comment that used to sit here described discardDraft, two functions
       * down — it had drifted onto the wrong declaration.)
       */
      beginEdit: (vehicleId) => set({ ...INITIAL_STATE, vehicleId, entryMode: 'edit' }),

      /** Explicit, user-confirmed discard: clears the draft AND its files. */
      discardDraft: () => {
        pruneListingPhotoFiles([])
        // The staged copies of those files are just as orphaned. Fire-and-forget: a discard
        // must work offline, and an object that survives this sweep is caught by the next one.
        void vehicleImageService.clearStagedListingPhotos()
        set(INITIAL_STATE)
      },
    }),
    {
      name: 'tago.listing-wizard.v1',
      // `photos` is the one field that can be megabytes: on web every capture is a `data:` URI
      // holding the JPEG itself. Split so a draft that does not fit WITH the photos still gets
      // saved without them, instead of the whole write being refused and the host silently
      // losing everything they typed.
      storage: createSplitPlatformStorage<PersistedListingWizard>('photos'),
      // Persist what the host TYPED or CAPTURED. documentReview mirrors server
      // rows and is re-fetched, so it stays out.
      partialize: (state): PersistedListingWizard => ({
        currentStep: state.currentStep,
        vehicleId: state.vehicleId,
        entryMode: state.entryMode,
        dirtySinceLoad: state.dirtySinceLoad,
        details: state.details,
        features: state.features,
        photos: state.photos,
        uploadedPhotoUris: state.uploadedPhotoUris,
        stagedPhotos: state.stagedPhotos,
        uploadedDocumentUris: state.uploadedDocumentUris,
        documentsNeedReview: state.documentsNeedReview,
        location: state.location,
        homeLocationPinnedByHost: state.homeLocationPinnedByHost,
        pricing: state.pricing,
        documents: state.documents,
        documentsSkipped: state.documentsSkipped,
        policyDeferred: state.policyDeferred,
        companyDetection: state.companyDetection,
        ownership: state.ownership,
      }),
      version: 1,
      // A restored draft must be truthful. Drafts written before captures became
      // durable (and any file the OS reclaimed since) point at URIs whose bytes
      // are gone; keeping those slots "filled" would only defer the failure to
      // publish. Drop them so the checklist shows exactly what still exists and
      // the host retakes only what is actually missing.
      onRehydrateStorage: () => (state) => {
        if (!state) return

        if (state.photos?.photos?.length) {
          const surviving = state.photos.photos.filter((photo) => listingPhotoExists(photo.uri))
          if (surviving.length !== state.photos.photos.length) {
            state.photos = { ...state.photos, photos: surviving }
          }
        }

        // DOCUMENTS TOO, and outside the photos guard. `documents` is persisted
        // wholesale, and persistListingCapture fails OPEN — when the copy cannot
        // be made it returns the evictable cache URI, which is precisely the
        // low-storage situation where Android then reclaims it. A restored draft
        // that still claims a scanned tarjeta whose file is gone shows a checked
        // step and dies at publish for a legally required document. Clear the
        // reference so the step honestly asks for the scan again.
        //
        // Cleared to `undefined`, NOT to ''. Both the documents step and review
        // rebuild the field as `documents.frontPhotoUri ?? (isEditMode ?
        // 'existing' : '')`, and `??` only falls through on null/undefined — so
        // an empty string SURVIVES the coalesce and the 'existing' sentinel is
        // never produced. In edit mode that turned an evicted local file into a
        // publish blocked on re-scanning a tarjeta the server already holds
        // approved, which the host cannot satisfy because the scan was never
        // theirs to retake. In create mode `undefined ?? ''` is '' either way.
        const documents = state.documents
        if (documents) {
          const cleared: Partial<typeof documents> = {}
          if (documents.frontPhotoUri && !listingPhotoExists(documents.frontPhotoUri)) {
            cleared.frontPhotoUri = undefined
          }
          if (documents.backPhotoUri && !listingPhotoExists(documents.backPhotoUri)) {
            cleared.backPhotoUri = undefined
          }
          if (
            documents.insurancePolicyPhotoUri &&
            !listingPhotoExists(documents.insurancePolicyPhotoUri)
          ) {
            cleared.insurancePolicyPhotoUri = undefined
          }
          if (Object.keys(cleared).length > 0) {
            state.documents = { ...documents, ...cleared }
          }
        }
      },
    },
  ),
)
